/**
 * 统一日志工具
 * --------------------------------------------------------------------
 * 用法：
 *   import { logger } from '@/utils/logger'
 *   logger.warn('xxx')
 *   logger.error('xxx', err)
 *
 * 设计目标：
 * 1. 所有环境全局禁止 console.log / info / debug。
 * 2. warn 按需保留，error 保留；两者的参数在输出前统一递归脱敏。
 * 3. 支持 warn / error / silent 分级，可通过 LOG_LEVEL 调整。
 */

const LEVELS = {
  silent: -1,
  error: 0,
  warn: 1,
  info: 2,
  debug: 3,
}

const MUTED_CONSOLE_METHODS = ['log', 'info', 'debug']
const OUTPUT_CONSOLE_METHODS = ['warn', 'error']
const ALL_CONSOLE_METHODS = [...MUTED_CONSOLE_METHODS, ...OUTPUT_CONSOLE_METHODS]
const REDACTED = '[REDACTED]'
const CIRCULAR = '[Circular]'
const MAX_SANITIZE_DEPTH = 6
const MAX_SANITIZE_KEYS = 100

const nativeConsoleMethods = {}
if (typeof console !== 'undefined') {
  ALL_CONSOLE_METHODS.forEach((method) => {
    if (typeof console[method] === 'function') {
      nativeConsoleMethods[method] = console[method].bind(console)
    }
  })
}

const SENSITIVE_ASSIGNMENT_PATTERN = /((?:x[_-]?api[_-]?key|api[_-]?key|api[_-]?secret|client[_-]?secret|app[_-]?id|access[_-]?token|refresh[_-]?token|authorization|password|passwd|secret|token|user[_-]?sign|signature|session|cookie|server[_-]?url|stream[_-]?url)\s*["']?\s*[:=]\s*)(?:"[^"]*"|'[^']*'|[^,;\s&}]+)/gi
const BEARER_PATTERN = /\bBearer\s+[A-Za-z0-9._~+/=-]+/gi

function isSensitiveKey(key) {
  const normalized = String(key).replace(/[^a-z0-9]/gi, '').toLowerCase()
  return (
    normalized.includes('secret') ||
    normalized.includes('password') ||
    normalized.includes('passwd') ||
    normalized.includes('token') ||
    normalized.includes('authorization') ||
    normalized.includes('signature') ||
    normalized.includes('usersign') ||
    normalized === 'sign' ||
    normalized === 'auth' ||
    normalized === 'cookie' ||
    normalized === 'session' ||
    normalized === 'apikey' ||
    normalized === 'xapikey' ||
    normalized === 'appid' ||
    normalized === 'serverurl' ||
    normalized === 'streamurl'
  )
}

function sanitizeString(value) {
  return value
    .replace(BEARER_PATTERN, `Bearer ${REDACTED}`)
    .replace(SENSITIVE_ASSIGNMENT_PATTERN, `$1${REDACTED}`)
}

/**
 * 返回适合写入日志的脱敏副本，不修改业务数据本身。
 */
export function sanitizeLogData(value, seen = new WeakSet(), depth = 0) {
  if (typeof value === 'string') return sanitizeString(value)
  if (value === null || typeof value !== 'object') return value
  if (depth >= MAX_SANITIZE_DEPTH) return '[MaxDepth]'
  if (seen.has(value)) return CIRCULAR

  seen.add(value)

  if (value instanceof Error) {
    const sanitizedError = {
      name: sanitizeString(value.name || 'Error'),
      message: sanitizeString(value.message || ''),
    }
    if (value.stack) sanitizedError.stack = sanitizeString(value.stack)
    Object.keys(value).slice(0, MAX_SANITIZE_KEYS).forEach((key) => {
      sanitizedError[key] = isSensitiveKey(key)
        ? REDACTED
        : sanitizeLogData(value[key], seen, depth + 1)
    })
    return sanitizedError
  }

  if (value instanceof Date) return value.toISOString()
  if (Array.isArray(value)) {
    return value.slice(0, MAX_SANITIZE_KEYS).map((item) => sanitizeLogData(item, seen, depth + 1))
  }

  const sanitized = {}
  Object.keys(value).slice(0, MAX_SANITIZE_KEYS).forEach((key) => {
    sanitized[key] = isSensitiveKey(key)
      ? REDACTED
      : sanitizeLogData(value[key], seen, depth + 1)
  })
  return sanitized
}

function resolveLevel() {
  const stored = typeof localStorage !== 'undefined' ? localStorage.getItem('LOG_LEVEL') : null
  if (stored && ['silent', 'error', 'warn'].includes(stored)) {
    return LEVELS[stored]
  }
  return LEVELS.warn
}

let currentLevel = resolveLevel()

/**
 * 安装全局 Console 策略。
 *
 * 这层策略也覆盖未接入 logger 的旧代码和第三方 SDK：低级别日志
 * 全部静默，warn / error 输出前统一脱敏。
 */
export function installConsolePolicy() {
  if (typeof console === 'undefined') return

  MUTED_CONSOLE_METHODS.forEach((method) => {
    try {
      console[method] = () => {}
    } catch (err) {
      // 某些运行环境可能不允许覆盖 console；不影响应用继续启动。
    }
  })

  OUTPUT_CONSOLE_METHODS.forEach((method) => {
    const nativeMethod = nativeConsoleMethods[method]
    if (!nativeMethod) return

    try {
      console[method] = (...args) => {
        nativeMethod(...args.map((arg) => sanitizeLogData(arg)))
      }
    } catch (err) {
      // 某些运行环境可能不允许覆盖 console；不影响应用继续启动。
    }
  })
}

function shouldLog(level) {
  return LEVELS[level] <= currentLevel
}

function fmtPrefix(level) {
  return `[${new Date().toISOString()}] [${level.toUpperCase()}]`
}

export const logger = {
  setLevel(level) {
    if (!['silent', 'error', 'warn'].includes(level)) return
    currentLevel = LEVELS[level]
    try {
      localStorage.setItem('LOG_LEVEL', level)
    } catch (err) {
      // ignore
    }
  },

  getLevel() {
    return Object.keys(LEVELS).find((k) => LEVELS[k] === currentLevel) || 'info'
  },

  error(...args) {
    if (!shouldLog('error')) return
    // eslint-disable-next-line no-console
    console.error(fmtPrefix('error'), ...args)
  },

  warn(...args) {
    if (!shouldLog('warn')) return
    // eslint-disable-next-line no-console
    console.warn(fmtPrefix('warn'), ...args)
  },

  info(...args) {
    if (!shouldLog('info')) return
    // eslint-disable-next-line no-console
    console.info(fmtPrefix('info'), ...args)
  },

  debug(...args) {
    if (!shouldLog('debug')) return
    // eslint-disable-next-line no-console
    console.debug(fmtPrefix('debug'), ...args)
  },

  /**
   * 根据全局策略，log / info / debug 在所有环境中都保持静默。
   */
  log(...args) {
    if (!shouldLog('info')) return
    // eslint-disable-next-line no-console
    console.log(fmtPrefix('info'), ...args)
  },
}

export default logger
